import React, { useState, useEffect } from "react";
import { X } from "lucide-react";
import { Button } from "./button";
import useCookieConsentStore from "@/stores/ui/cookieConsentStore";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";

const CookieConsent = () => {
  const [isVisible, setIsVisible] = useState(false);
  const { hasConsented, setConsent } = useCookieConsentStore();

  useEffect(() => {
    // Show the banner only if consent hasn't been given
    if (!hasConsented) {
      // Small delay to ensure it doesn't flash immediately on load
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [hasConsented]);

  const handleAccept = () => {
    setConsent(true);
    setIsVisible(false);
  };

  const handleDismiss = () => {
    setConsent(true);
    setIsVisible(false);
  };

  // Animation variants
  const variants = {
    hidden: {
      opacity: 0,
      y: 50,
      transition: {
        duration: 0.3,
        ease: "easeInOut",
      },
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.3,
        ease: "easeOut",
      },
    },
    exit: {
      opacity: 0,
      y: 50,
      transition: {
        duration: 0.3,
        ease: "easeInOut",
      },
    },
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          className="fixed bottom-4 right-0 w-2/3 px-4 z-50"
          initial="hidden"
          animate="visible"
          exit="exit"
          variants={variants}
        >
          <div className="mx-auto max-w-8xl">
            <div className="backdrop-blur-xl bg-white/60 text-neutral-800 rounded-xl shadow-lg p-4 flex items-center justify-between border border-white/10">
              <div className="flex-1">
                <p className="text-xs md:text-sm font-light">
                  We use cookies to enhance your browsing experience. By
                  continuing to use our site, you agree to our use of cookies.
                  Read our{" "}
                  <Link
                    to="/privacy-policy"
                    className="underline hover:text-neutral-900 transition-colors font-medium"
                  >
                    Privacy Policy
                  </Link>{" "}
                  for more information.
                </p>
              </div>
              <div className="flex items-center space-x-2 ml-6">
                <Button
                  variant="default"
                  size="sm"
                  onClick={handleAccept}
                  className="bg-white text-indigo-800 ring-1 ring-indigo-800 hover:bg-indigo-800 hover:text-white transition-colors rounded-lg"
                  aria-label="Accept cookies"
                >
                  <span className="text-xs sm:text-sm">Dismiss</span>
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleDismiss}
                  className="text-xs sm:text-sm bg-white text-neutral-800 ring-1 ring-neutral-800 hover:bg-neutral-800 hover:text-white transition-colors rounded-lg"
                  aria-label="Dismiss cookie notice"
                >
                  <X className="h-5 w-5" />
                </Button>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CookieConsent;
